/**
 * free-gift.js -- Custom Theme Edition
 * -----------------------------------------------------------------------------
 * Automatically adds / removes a free gift from the cart based on two rules:
 *   1. One or more "trigger" products must be present in the cart
 *   2. The cart subtotal (excluding the gift itself) must meet a minimum value
 *
 * This version is built for a standard Shopify cart page (no AJAX cart /
 * cart drawer). It runs on every page load, evaluates the rules, and reloads
 * the page once if the cart needs to change.
 * -----------------------------------------------------------------------------
 */

const FREE_GIFT_CONFIG = {
  rules: [
    {
      // Sunday Table Box -- free gift when cart >= $400
      giftVariantId: 66731113513005,
      triggerProductIds: [15096704696365],
      requireAllTriggers: false,
      minimumCartValueCents: 40000,
    },
  ],

  // Set to false in production
  debug: false,
};

// -----------------------------------------------------------------------------
// CORE ENGINE  --  no changes needed below this line
// -----------------------------------------------------------------------------

(function () {
  "use strict";

  const log = (...args) =>
    FREE_GIFT_CONFIG.debug && console.log("[FreeGift]", ...args);

  // -- Cart API helpers --------------------------------------------------------

  async function getCart() {
    const res = await fetch("/cart.js", {
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) throw new Error("Failed to fetch cart");
    return res.json();
  }

  async function addVariantToCart(variantId) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    if (!res.ok) throw new Error(`Failed to add variant ${variantId}`);
    return res.json();
  }

  async function removeCartItemByKey(itemKey) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: itemKey, quantity: 0 }),
    });
    if (!res.ok) throw new Error(`Failed to remove item ${itemKey}`);
    return res.json();
  }

  // -- Rule evaluation helpers -------------------------------------------------

  function getSubtotalExcludingGifts(cart, giftVariantIds) {
    return cart.items.reduce((total, item) => {
      if (giftVariantIds.includes(item.variant_id)) return total;
      return total + item.final_line_price;
    }, 0);
  }

  function triggerProductsPresent(cart, rule) {
    const cartProductIds = cart.items.map((item) => item.product_id);
    log("Cart product IDs:", cartProductIds);
    log("Trigger product IDs:", rule.triggerProductIds);
    if (rule.requireAllTriggers) {
      return rule.triggerProductIds.every((id) => cartProductIds.includes(id));
    }
    return rule.triggerProductIds.some((id) => cartProductIds.includes(id));
  }

  function findGiftItem(cart, variantId) {
    return cart.items.find((item) => item.variant_id === variantId) || null;
  }

  // -- Main evaluation loop ----------------------------------------------------

  async function evaluateGiftRules() {
    try {
      const cart = await getCart();
      const allGiftVariantIds = FREE_GIFT_CONFIG.rules.map((r) => r.giftVariantId);
      const subtotal = getSubtotalExcludingGifts(cart, allGiftVariantIds);

      log("Cart state:", cart);
      log("Subtotal (excl. gifts):", subtotal);

      // Group rules by giftVariantId so all rules for the same gift are
      // evaluated together -- gift is only removed if NO rule is satisfied
      const giftVariantIds = [...new Set(allGiftVariantIds)];
      let cartChanged = false;

      for (const giftVariantId of giftVariantIds) {
        const rulesForGift = FREE_GIFT_CONFIG.rules.filter(
          (r) => r.giftVariantId === giftVariantId
        );
        const giftItem = findGiftItem(cart, giftVariantId);

        const shouldHaveGift = rulesForGift.some((rule) => {
          const triggersPresent = triggerProductsPresent(cart, rule);
          const meetsMinimum = subtotal >= rule.minimumCartValueCents;
          log(`Rule for variant ${giftVariantId}:`, {
            triggersPresent,
            meetsMinimum,
            giftInCart: !!giftItem,
            shouldHaveGift: triggersPresent && meetsMinimum,
          });
          return triggersPresent && meetsMinimum;
        });

        if (shouldHaveGift && !giftItem) {
          log("Adding gift variant:", giftVariantId);
          await addVariantToCart(giftVariantId);
          cartChanged = true;
        } else if (!shouldHaveGift && giftItem) {
          log("Removing gift item with key:", giftItem.key);
          await removeCartItemByKey(giftItem.key);
          cartChanged = true;
        }
      }

      // Reload the page once so the cart template re-renders with the changes
      if (cartChanged) {
        log("Cart changed -- reloading page");
        window.location.reload();
      }
    } catch (err) {
      console.error("[FreeGift] Error evaluating gift rules:", err);
    }
  }

  // -- Init --------------------------------------------------------------------
  // Run on every page load. Since this is a standard cart page with no AJAX,
  // we just evaluate once when the DOM is ready.

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", evaluateGiftRules);
  } else {
    evaluateGiftRules();
  }

})();
